import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Lock, PlayCircle, ShieldCheck, AlertCircle } from 'lucide-react';
import { api, endpoints } from '@/lib/api-client';
import { PolicyBuilder, PolicyData } from '@/components/security/PolicyBuilder';
import { PolicyList, CedarPolicy } from '@/components/security/policies';
import { PolicyTemplateGallery } from '@/components/security/templates/PolicyTemplateGallery';
import { EnhancedSimulator, SimulationRequest } from '@/components/security/simulator';
import { SimulationResult } from '@/components/security/simulator/SimulationDiagnostics';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export const Route = createFileRoute('/_dashboard/admin/org/policies')({
  component: OrgPoliciesPage,
});

function OrgPoliciesPage() {
  const [policies, setPolicies] = useState<CedarPolicy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableRoles] = useState(['admin', 'developer', 'viewer', 'member', 'billing']);

  // Fetch policies on mount
  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        setIsLoading(true);
        const data = await api.get<CedarPolicy[]>(endpoints.adminCedarPolicies);
        setPolicies(data || []);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch policies:', err);
        setError('Failed to load policies. Please try again.');
        setPolicies([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPolicies();
  }, []);

  // Handle saving a new policy
  const handleSavePolicy = useCallback(async (data: PolicyData) => {
    try {
      const newPolicy = await api.post<CedarPolicy>(endpoints.adminCedarPolicies, {
        name: data.name,
        description: data.description,
        policy_text: data.policyText,
        is_active: true,
      });
      setPolicies((prev) => [...prev, newPolicy]);
    } catch (err) {
      console.error('Failed to save policy:', err);
      setError('Failed to save policy. Please try again.');
    }
  }, []);

  // Handle deleting a policy
  const handleDeletePolicy = useCallback(async (id: string) => {
    try {
      await api.delete(`${endpoints.adminCedarPolicies}/${id}`);
      setPolicies((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error('Failed to delete policy:', err);
      setError('Failed to delete policy. Please try again.');
    }
  }, []);

  // Handle simulation
  const handleSimulate = useCallback(async (request: SimulationRequest): Promise<SimulationResult> => {
    try {
      const result = await api.post<SimulationResult>(`${endpoints.adminCedarPolicies}/simulate`, request);
      return result;
    } catch (err) {
      console.error('Simulation failed:', err);
      return {
        allowed: false,
        reason: 'Simulation failed due to an error',
        matchedPolicies: [],
      };
    }
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Security Policies</h1>
          <p className="text-muted-foreground">
            Configure Cedar-based access control policies
          </p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="px-3 py-1 bg-green-50 text-green-700 border-green-200">
            <ShieldCheck className="w-3 h-3 mr-1" /> Cedar Active
          </Badge>
          <Badge variant="secondary" className="px-3 py-1">
            {policies.length} policies
          </Badge>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Main Tabs */}
      <Tabs defaultValue="policies" className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-[450px]">
          <TabsTrigger value="policies">
            <Lock className="w-4 h-4 mr-2" /> Rules
          </TabsTrigger>
          <TabsTrigger value="templates">
            <FileText className="w-4 h-4 mr-2" /> Templates
          </TabsTrigger>
          <TabsTrigger value="simulator">
            <PlayCircle className="w-4 h-4 mr-2" /> Simulator
          </TabsTrigger>
        </TabsList>

        {/* POLICIES TAB */}
        <TabsContent value="policies" className="mt-6 space-y-6">
          {/* Policy Builder */}
          <Card>
            <CardHeader>
              <CardTitle>Create New Policy</CardTitle>
              <CardDescription>
                Define a new access control policy using Cedar syntax
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PolicyBuilder onSave={handleSavePolicy} />
            </CardContent>
          </Card>

          {/* Policy List */}
          <Card>
            <CardHeader>
              <CardTitle>Active Policies</CardTitle>
              <CardDescription>
                Manage your organization's security policies
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PolicyList
                policies={policies}
                onEdit={() => {}} // Edit handled via PolicyEditDialog in full implementation
                onDelete={handleDeletePolicy}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* TEMPLATES TAB */}
        <TabsContent value="templates" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Policy Templates</CardTitle>
              <CardDescription>
                Start from pre-built policy templates for common use cases
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PolicyTemplateGallery onSelectTemplate={() => {}} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* SIMULATOR TAB */}
        <TabsContent value="simulator" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Policy Simulator</CardTitle>
              <CardDescription>
                Test how policies would evaluate for specific requests
              </CardDescription>
            </CardHeader>
            <CardContent>
              <EnhancedSimulator
                onSimulate={handleSimulate}
                availableRoles={availableRoles}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
